import { useEffect, useCallback } from 'react';
import { useResourceFilters } from './useResourceFilters';
import { useResourcePagination } from './useResourcePagination';
import { useResourceSelection } from './useResourceSelection';
import { useResourceApi } from './useResourceApi';

interface UseResourcesListParams {
  itemsPerPage: number;
}

export type ResourceType = 'link' | 'file';
export type ResourceCategory = 'document' | 'media' | 'template' | 'guide' | 'other';

const useResourcesList = ({ itemsPerPage }: UseResourcesListParams) => {
  const {
    agencyFilter,
    typeFilter,
    categoryFilter,
    handleAgencyFilterChange,
    handleTypeFilterChange,
    handleCategoryFilterChange,
  } = useResourceFilters();

  const { currentPage, onPageChange, setCurrentPage } = useResourcePagination(1);

  const { selected, onRowSelect, setSelected } = useResourceSelection();

  const {
    resourcesData,
    fetchResources,
    downloadResource,
    deleteResource,
    deleteMultipleResources,
  } = useResourceApi({
    agencyFilter,
    typeFilter,
    categoryFilter,
    currentPage,
    itemsPerPage,
  });

  const onAgencyFilterChange = useCallback(
    (value: string) => {
      handleAgencyFilterChange(value);
      setCurrentPage(1);
    },
    [handleAgencyFilterChange, setCurrentPage]
  );

  const onTypeFilterChange = useCallback(
    (value: string) => {
      handleTypeFilterChange(value);
      setCurrentPage(1);
    },
    [handleTypeFilterChange, setCurrentPage]
  );

  const onCategoryFilterChange = useCallback(
    (value: string) => {
      handleCategoryFilterChange(value);
      setCurrentPage(1);
    },
    [handleCategoryFilterChange, setCurrentPage]
  );

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  return {
    agencyFilter,
    typeFilter,
    categoryFilter,
    currentPage,
    resourcesData,
    selectedResources: selected,
    onAgencyFilterChange,
    onTypeFilterChange,
    onCategoryFilterChange,
    onPageChange,
    onRowSelect,
    onDownloadResource: downloadResource,
    onDeleteResource: deleteResource,
    onDeleteMultipleResources: deleteMultipleResources,
    setSelected,
    fetchResources,
  };
};

export default useResourcesList;
